import { WorkLaunchEventName } from '@/models/analytics/features/workLaunchEvents';
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import {
  createEventSpecification,
  trackWorkApprovalActionedSpec,
  trackWorkErrorEncounteredSpec,
  trackWorkMessageActionedSpec,
  trackWorkMessageSubmittedSpec,
  trackWorkRunCompletedSpec,
  trackWorkSurfaceActionedSpec,
  trackWorkTermsActionedSpec,
  trackWorkWorkflowActionedSpec,
  type WorkApprovalActioned,
  type WorkErrorEncountered,
  type WorkMessageActioned,
  type WorkMessageSubmitted,
  type WorkRunCompleted,
  type WorkSurfaceActioned,
  type WorkTermsActioned,
  type WorkWorkflowActioned,
} from '@snowtype/snowplow';

import type { SnowplowEventMap } from '../trackSnowplowEvent';

/**
 * Hand-rolled self-describing emits for Work schemas whose Iglu URI or payload
 * moved ahead of snowtype/snowplow.ts (additionalProperties: false). Sending new
 * properties on the old URI fails enrichment.
 *
 * TODO(snowtype): swap each branch to its generated Spec after
 * `rm .snowtype-lock.json && npx @snowplow/snowtype generate`.
 */
const AGENTS_DATA_PRODUCT_ID = '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd';

/** Event-spec ids and versions from the generated Snowtype specs. Hand-rolled emits must match. */
const WORK_EVENT_SPECS = {
  [WorkLaunchEventName.WorkArtifactActioned]: {
    id: 'faa6755e-d01d-5425-9219-0f42b45c75db',
    version: 1,
  },
  [WorkLaunchEventName.WorkExperienceFeedbackActioned]: {
    id: '40e68f36-846d-4435-af1d-07b8d59dd11b',
    version: 2,
  },
  [WorkLaunchEventName.WorkUpgradeActioned]: {
    id: '049e3dda-9700-5a79-a6e8-2d16ec05e570',
    version: 0,
  },
} as const;

function trackHandRolledWorkEvent(
  eventName: keyof typeof WORK_EVENT_SPECS,
  schema: string,
  props: unknown
): void {
  const spec = WORK_EVENT_SPECS[eventName];
  const eventSpecificationContext = createEventSpecification({
    id: spec.id,
    version: spec.version,
    name: eventName,
    data_product_id: AGENTS_DATA_PRODUCT_ID,
    data_product_name: 'agents',
  });
  trackSelfDescribingEvent({
    event: { schema, data: props as Record<string, unknown> },
    context: [eventSpecificationContext],
  });
}

export const workEvents: SnowplowEventMap = {
  [WorkLaunchEventName.WorkSurfaceActioned]: (props) =>
    trackWorkSurfaceActionedSpec(props as WorkSurfaceActioned),
  [WorkLaunchEventName.WorkTermsActioned]: (props) =>
    trackWorkTermsActionedSpec(props as WorkTermsActioned),
  [WorkLaunchEventName.WorkMessageSubmitted]: (props) =>
    trackWorkMessageSubmittedSpec(props as WorkMessageSubmitted),
  [WorkLaunchEventName.WorkRunCompleted]: (props) =>
    trackWorkRunCompletedSpec(props as WorkRunCompleted),
  [WorkLaunchEventName.WorkWorkflowActioned]: (props) =>
    trackWorkWorkflowActionedSpec(props as WorkWorkflowActioned),
  // Schema 1-1-0 — action enum includes `created`; adds plan/seat.
  [WorkLaunchEventName.WorkArtifactActioned]: (props) =>
    trackHandRolledWorkEvent(
      WorkLaunchEventName.WorkArtifactActioned,
      'iglu:com.heidi/work_artifact_actioned/jsonschema/1-1-0',
      props
    ),
  [WorkLaunchEventName.WorkApprovalActioned]: (props) =>
    trackWorkApprovalActionedSpec(props as WorkApprovalActioned),
  [WorkLaunchEventName.WorkErrorEncountered]: (props) =>
    trackWorkErrorEncounteredSpec(props as WorkErrorEncountered),
  [WorkLaunchEventName.WorkMessageActioned]: (props) =>
    trackWorkMessageActionedSpec(props as WorkMessageActioned),
  // Schema 1-0-2 — adds feedback_count, plan/seat.
  [WorkLaunchEventName.WorkExperienceFeedbackActioned]: (props) =>
    trackHandRolledWorkEvent(
      WorkLaunchEventName.WorkExperienceFeedbackActioned,
      'iglu:com.heidi/work_experience_feedback_actioned/jsonschema/1-0-2',
      props
    ),
  // New event — no generated Spec yet.
  [WorkLaunchEventName.WorkUpgradeActioned]: (props) =>
    trackHandRolledWorkEvent(
      WorkLaunchEventName.WorkUpgradeActioned,
      'iglu:com.heidi/work_upgrade_actioned/jsonschema/1-0-0',
      props
    ),
};
